// Invoice queue — card gallery (app.md §8, redesigned v1.20). One card per
// invoice: a clickable header row (chevron · company eyebrow over vendor title
// with a date/external-id meta line · age + flags or amounts · actions) that
// expands to the inline line items sub-table (lazy-loaded). A slim toolbar above
// the list carries Select all (bulk screens) and Expand all / Collapse all —
// the old table-header controls, relocated; a host that wants the expander in
// its own group header instead passes `expandAll` and the toolbar steps aside
// (Export Control, Closing Confirmation). Two right-cluster variants:
// 'intake' (age pill, plus whatever `intakeBadge` asks for) and 'amounts'
// (downstream steps: headline Total; the age pill moves into the meta line).
// Optional selection checkboxes drive bulk actions; per-row buttons
// (Approve/Deny/Confirm/Archive…) come in via rowActions, rendered beside the
// Open button. An optional totals footer sums the displayed rows' amounts
// (Closing Confirmation, Export Control — app.md §9).
//
// THE EXPANDED CARD IS EDITABLE ON GL CODING where the host says so (owner item
// 22, 2026-08-31): pass `editAccounts` / `editDepartments` and the line-item
// table's "Account" and "Department" columns become the same pickers the detail
// modal offers, so a reviewer can code a line without opening the invoice. The
// write, the re-read and the refusal message all live here rather than in each
// host, because this component owns the line-item cache they invalidate.
//
// PORT NOTE: a row no longer shows "Submitting…". Decisions are synchronous —
// the write returns the new status — so a row is either still here or already
// gone, and there is no in-between state to render. The flag chips read the
// row's own unresolved flag types too; the queue used to fire a second query.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import {
  Button,
  Checkbox,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  ChevronDoubleDownRegular,
  ChevronDoubleUpRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons'
import type { InvoiceLineItem, Invoice } from '../lib/types'
import {
  applyLineItemDepartmentEdit,
  applyLineItemMarketingDetailEdit,
  applyLineItemAccountEdit,
  changeLineGroups,
  loadLineItems,
  loadLineItemsForInvoices,
  unresolvedFlagLabels,
  unresolvedFlagTypes,
} from '../lib/data'
import { errorMessage } from '../api/errors'
import { TERMINAL_STATUSES, groupingOpen } from '@ap/shared'
import type { LineGroupOperation } from '../api/types'
import { ageInDays, formatCurrency, formatDate, type LineItemDetailScreen } from '../lib/format'
import { EXPANDED_HEADER_FILL, TINT_PILLS, useQueueToolbarButtonStyles } from '../lib/theme'
import { planLineItemRefresh } from '../lib/refresh'
import { AgePill, DotPill, StatusBadge, UncodedLinesPill, UnresolvedFlagsPill } from './Badges'
import { InvoiceLineItemsTable } from './InvoiceLineItemsTable'
import { categoryPickableAt, glCodeEditable } from '../lib/required'
import { HELD_PILL } from '../lib/holds'
import { ErrorBanner } from './States'

const useStyles = makeStyles({
  // NO BUTTON LABEL WRAPS (QA 2026-10-04, C2-05): every button and the
  // Select all checkbox keep one line and their width; a caption beside them
  // (the amber coding gate) is what wraps. `Deny 1 Selected` used to break
  // onto two lines to make room for it.
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    '& button, & .fui-Checkbox': { whiteSpace: 'nowrap', flexShrink: 0 },
  },
  spacer: { flexGrow: 1 },
  list: { display: 'flex', flexDirection: 'column', gap: '12px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    boxShadow: tokens.shadow2,
    overflow: 'hidden',
    transitionProperty: 'box-shadow',
    transitionDuration: '150ms',
    '&:hover': { boxShadow: tokens.shadow4 },
  },
  dimmed: { opacity: 0.55 },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '12px 16px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  // Light brand tint marks the expanded card's header as part of the open group.
  headOpen: {
    backgroundColor: EXPANDED_HEADER_FILL,
    '&:hover': { backgroundColor: '#F0F6FC' },
  },
  chev: {
    display: 'flex',
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    transitionProperty: 'transform',
    transitionDuration: '150ms',
    '@media (prefers-reduced-motion: reduce)': { transitionProperty: 'none' },
  },
  chevOpen: { transform: 'rotate(90deg)' },
  // The expander is a real button (the card header is not) — trim it to the
  // glyph so the row still reads as a chevron.
  chevBtn: { minWidth: '24px', padding: 0, flexShrink: 0 },
  tgroup: { flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' },
  eyebrow: {
    fontSize: '10.5px',
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: tokens.colorNeutralForeground3,
  },
  vendor: {
    fontSize: '15px',
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '12.5px',
    color: tokens.colorNeutralForeground3,
  },
  rgroup: { display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 },
  amounts: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '2px',
    fontVariantNumeric: 'tabular-nums',
  },
  totalLine: { fontSize: '15px', fontWeight: tokens.fontWeightSemibold },
  actions: { display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 },
  // 0fr→1fr grid trick animates the expansion without measuring heights.
  exp: {
    display: 'grid',
    gridTemplateRows: '0fr',
    transitionProperty: 'grid-template-rows',
    transitionDuration: '180ms',
    '@media (prefers-reduced-motion: reduce)': { transitionProperty: 'none' },
  },
  expOpen: { gridTemplateRows: '1fr' },
  clip: { overflow: 'hidden', minHeight: 0 },
  lineItemsWrap: {
    margin: '0 16px',
    padding: '10px 0 14px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  // A refused inline coding write, inside the card it was refused on. The
  // queue has no action bar to put it in and a toast would name no row.
  codingError: { marginBottom: '8px' },
})

interface Props {
  invoices: Invoice[]
  /** Which screen this queue is mounted on — passed straight through to the
      expanded card's `InvoiceLineItemsTable`, which needs it to decide which
      empty required fields are red (`../lib/required.ts`). Required: every
      Invoices-tab caller of this queue knows its own screen. Also decides
      whether the card's Additional Detail can be edited
      (`categoryPickableAt`, `../lib/required.ts` — D-192). */
  screen: LineItemDetailScreen
  /** 'intake' (default) = Age / Flags-or-Status; 'amounts' = Total (plus a
      terminal-status badge on Archives). The Subtotal + Tax breakdown that
      used to print under the total here was removed 2026-08-14 — it
      duplicated ReconcileFooter's line in the expanded card. */
  columns?: 'intake' | 'amounts'
  selectable?: boolean
  selectedIds?: Set<string>
  onToggle?: (id: string) => void
  onToggleAll?: () => void
  allSelected?: boolean
  /** What the 'intake' right cluster shows BESIDE the age pill.

      `'flags'` is the unresolved-flags pill, reading its count and its
      tooltip's types off the row itself — Import Control, where resolving
      flags is the work (D-219). `'status'` (the default) is the status badge.
      `'none'` is neither: Import Control asked for it while the step settled
      the Company alone (owner item 14, 2026-08-31: "Flags do not really
      matter at this step … we can hide the flags pill on this screen"), and
      no screen passes it since the merge (D-215) brought the flags back. */
  intakeBadge?: 'flags' | 'status' | 'none'
  /** Department/Director/Oversight Approval only: resolves a row's coding-gate
      pill — how many active line items still need "What is it?" or "Who is it
      for?", plus the tooltip to show. MUST be resolved through the host's own
      gate helper (`ApprovalScreen`'s `uncodedCount`/`uncodedReason`, D-9), never
      by reading `uncoded_line_item_count` here directly, so the pill can never
      disagree with the Approve button beside it. Renders as an amber "N lines
      to finish" pill in the meta line, next to `AgePill`; omitted at count 0.
      Leave unset for queues the coding gate doesn't apply to. */
  uncodedLinesFor?: (invoice: Invoice) => { count: number; tooltip?: string }
  onOpen?: (invoice: Invoice) => void
  /** Per-row action buttons rendered before Open (Approve/Deny/Confirm/Archive…). */
  rowActions?: (invoice: Invoice) => ReactNode
  /** Per-row selection veto: return the reason (tooltip) to disable the
      checkbox — not authorized, on hold, … — or undefined to allow. */
  selectionDisabled?: (invoice: Invoice) => string | undefined
  /** Cards rendered dimmed (on hold today). Purely visual. */
  dimmedIds?: Set<string>
  /** Invoices on hold — they carry an "On hold" pill, because dimming
      and an amber lock glyph have no text equivalent on their own. Separate
      from dimmedIds, which callers may use for other states. */
  heldIds?: Set<string>
  /** Bump to re-read the expanded cards' line items — the queue's own reloads
      no longer invalidate them (see the cache effect below). Hosts that edit
      lines from the detail modal bump this on save. */
  lineItemsNonce?: number
  /** EXPAND ALL, LIFTED TO THE HOST (owner, 2026-08-28: "move the Expand all
      button to be inline to the left of the Export button"). Set it and this
      queue renders no toolbar row of its own — the host owns the button and
      passes the state down. `true` expands every card (loading each one's
      line items), `false` collapses them all; per-card chevrons keep working
      either way, so this is a REQUEST, not the truth of what is open. Leave
      unset (the default) and the queue keeps its own Expand all / Collapse
      all button, unchanged for every other screen. */
  expandAll?: boolean
  /** THE SCREEN'S BULK ACTIONS, INLINE WITH EXPAND ALL (owner, 2026-09-15:
      "for each of the screens that carry Approve Selected and Deny Selected
      buttons, please move those buttons to be inline with and to the right of
      Expand all"). They sat in an action bar of their own above this queue,
      whose left half was empty by design and whose right half repeated the row
      the queue's own Select all already opened — two bars, one of them almost
      entirely white space.

      Rendered LAST in the toolbar, after Expand all, so the row reads
      left-to-right as what is selected, what is shown, and what to do about it.
      Export Control and Closing Confirmation already grouped by company and
      built this same row per group by hand; this is the ungrouped screens
      catching up.

      Setting this makes the toolbar render even where it otherwise would not —
      a screen can carry bulk actions without being `selectable`, though none
      does today. */
  toolbarActions?: ReactNode
  /** How the expanded card's line-items table shows GL coding — a straight
      pass-through to `InvoiceLineItemsTable`'s own `codingDisplay` (owner item
      7, 2026-08-19: "Account is still showing for invoices that are expanded
      in the Export Control. Account should never appear anywhere in the
      Invoices tab."). This card never received an edit callback for the
      Code / Account / Department columns — it is always the read-only
      expanded gallery view — so it was ALWAYS showing the composed Code
      column by default, on every Invoices-tab screen that uses this
      component, not only Export Control. Defaults to `'code'` (zero
      behaviour change); every Invoices-tab caller now sets this explicitly.

      `'account-only'` was AP/AR Approval's shape: "What is it?" alone, with
      no "Who is it for?" column at all. No screen passes it since that step
      merged into Import Control (D-215), which offers the department too. */
  codingDisplay?: 'code' | 'halves' | 'account-only'
  /** OWNER ITEM 22 (2026-08-31) — offer the "Account" ("What is it?") picker
      inline in the expanded card, instead of read-only text.

      THE SAME GRANT AS THE MODAL'S, NEVER WIDER. A host passes exactly the
      flag its own step already passes to `InvoiceDetailModal`
      (`editAccounts`), so this offers no control the server would refuse.
      Closing Confirmation / Denial Review / Archives are read-only; none of
      them pass either flag, and their cards are unchanged.

      NARROWED PER ROW by the route lock (`glCodeEditable`, `../lib/required.ts`
      — D-305): the flag says the step offers the picker at all; the row's own
      route says whether its Account or Department is still open.

      A HELD ROW NEVER GETS THE PICKER, whichever flags are set: a hold blocks
      every GL write path, so the control is dropped for any id in `heldIds`
      and that row's coding renders read-only exactly as before. */
  editAccounts?: boolean
  /** The "Department" ("Who is it for?") half of the same grant — see
      `editAccounts`. Import Control passes both; both are required at its
      release (D-309). */
  editDepartments?: boolean
  /** Called after an inline coding write the server APPLIED. The host reloads
      its own queue with it: a coding write changes `uncoded_line_item_count`,
      which is what the coding-gate pill and the Approve button beside it
      read. Not called on a refusal — nothing changed. */
  onCodingChanged?: () => void
}

export function InvoiceQueue({
  invoices,
  screen,
  columns = 'intake',
  selectable,
  selectedIds,
  onToggle,
  onToggleAll,
  allSelected,
  intakeBadge = 'status',
  uncodedLinesFor,
  onOpen,
  rowActions,
  selectionDisabled,
  dimmedIds,
  heldIds,
  lineItemsNonce = 0,
  expandAll,
  toolbarActions,
  codingDisplay = 'code',
  editAccounts,
  editDepartments,
  onCodingChanged,
}: Props) {
  const s = useStyles()
  const toolbarBtn = useQueueToolbarButtonStyles()
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  // 'failed': the first read of a card's lines failed. Shown as an error with
  // Retry, never as an empty list — "No line items on this invoice" is a
  // statement about the invoice, and a failed read cannot make it (code
  // review D-01).
  const [lineItemsById, setLineItemsById] = useState<Record<string, InvoiceLineItem[] | 'loading' | 'failed'>>({})
  // Line item whose coding write is in flight — disables that row's pickers.
  const [savingLineItemId, setSavingLineItemId] = useState<string | undefined>(undefined)
  // Refusals and picker remounts, both PER INVOICE: a refusal belongs on the
  // card it happened on, and remounting one card's pickers must not throw away
  // what somebody is typing in another card's.
  // By invoice: a failed coding write's message and the line and control it
  // failed on, so the card's table ties it to that picker (C1-09).
  const [codingErrors, setCodingErrors] = useState<Record<string, { message: string; lineItemId: string; field: string }>>({})
  const refusalIdBase = useId()
  const codingErrorId = (invoiceId: string) => `${refusalIdBase}-refusal-${invoiceId}`
  const [pickerResets, setPickerResets] = useState<Record<string, number>>({})

  // WHERE AN INLINE PICKER'S LISTBOX MOUNTS. `EntityPicker` self-resolves an
  // enclosing `[role="dialog"]` and falls back to `inlinePopup` when there is
  // none — and inline is wrong here: the listbox would open inside three
  // clipping ancestors in a row (the card's own `overflow: hidden`, the
  // expander's `clip`, and the line-item table's horizontal scroller), so it
  // would render as a sliver or not at all. Mount it at the nearest
  // `FluentProvider` instead — outside all three, and still inside the theme,
  // which `document.body` would not be: the design tokens every Fluent style
  // reads are custom properties declared on that element.
  const rootRef = useRef<HTMLDivElement>(null)
  const [pickerHost, setPickerHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setPickerHost((rootRef.current?.closest('.fui-FluentProvider') as HTMLElement | null) ?? null)
  }, [])

  const hasActions = !!(onOpen || rowActions)
  const allExpanded = invoices.length > 0 && invoices.every((i) => expandedIds.has(i.id))

  // Cached line items are invalidated when the rows actually change — a modal
  // save may have changed them, and a stale cache showed pre-edit lines (and a
  // bogus "doesn't reconcile" chip). Key on a stable signature (each row's id +
  // its last-write stamp) plus the host's lineItemsNonce, NOT the invoices array
  // identity: a reload hands back a fresh array of the same unchanged rows, and
  // keying on identity dumped and refetched every expanded card each time.
  //
  // PER CARD, AND WITHOUT A SPINNER (D-160). The gallery now refreshes every 30
  // seconds in the background, and this used to set EVERY expanded card back to
  // "loading" whenever any row in the list joined, left or changed. Now only a
  // card whose own row changed (or every card, when the host bumps the nonce) is
  // re-read, and it keeps its current line items on screen until the new ones
  // arrive. `planLineItemRefresh` in `lib/refresh.ts` holds the rule.
  const lineItemsKey =
    invoices.map((i) => `${i.id}:${i.updated_at ?? ''}`).join(',') + `#${lineItemsNonce}`
  const stampsRef = useRef<Map<string, string>>(new Map())
  const nonceRef = useRef(lineItemsNonce)
  useEffect(() => {
    const current = new Map(invoices.filter((i) => i.id).map((i) => [i.id, i.updated_at ?? '']))
    const { refetch, drop } = planLineItemRefresh({
      previous: stampsRef.current,
      current,
      nonceChanged: nonceRef.current !== lineItemsNonce,
      expanded: expandedIds,
      cached: Object.keys(lineItemsById),
    })
    stampsRef.current = current
    nonceRef.current = lineItemsNonce
    if (drop.length > 0 || refetch.some((id) => !Array.isArray(lineItemsById[id]))) {
      setLineItemsById((p) => {
        const next = { ...p }
        for (const id of drop) delete next[id]
        // Only a card with nothing on screen yet shows the spinner.
        for (const id of refetch) if (!Array.isArray(next[id])) next[id] = 'loading'
        return next
      })
    }
    fetchLineItems(refetch)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on lineItemsKey (stable row signature), not the invoices array identity
  }, [lineItemsKey])

  /** ONE READ FOR MANY CARDS (code review D-12): `GET /line-items?invoice_ids=`
      instead of one request per card, so Expand All on a 50-card queue is one
      call, not 50. The client splits at the route's 200-id cap. An invoice
      with no lines gets `[]`. On failure a card keeps the lines it already
      shows; a card with none is marked 'failed'. */
  function fetchLineItems(ids: readonly string[]) {
    if (ids.length === 0) return
    loadLineItemsForInvoices([...ids])
      .then((lines) => {
        const byInvoice: Record<string, InvoiceLineItem[]> = Object.fromEntries(ids.map((id) => [id, []]))
        for (const line of lines) byInvoice[line.invoice_id]?.push(line)
        setLineItemsById((p) => ({ ...p, ...byInvoice }))
      })
      .catch(() =>
        setLineItemsById((p) => {
          const next = { ...p }
          for (const id of ids) if (!Array.isArray(next[id])) next[id] = 'failed'
          return next
        }),
      )
  }

  // The host's Expand all / Collapse all request (see the `expandAll` prop).
  // Deliberately keyed on the flag ALONE: a queue reload hands back a fresh
  // array of the same rows, and depending on `invoices` here would re-expand
  // every card the reviewer had just collapsed by hand.
  useEffect(() => {
    if (expandAll === undefined) return
    if (!expandAll) {
      setExpandedIds(new Set())
      return
    }
    setExpandedIds(new Set(invoices.map((i) => i.id)))
    loadLineItemsFor(invoices)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a request, applied when the flag flips; not re-applied on every reload
  }, [expandAll])

  /** The first read of each card's lines, as one request. A card already
      read or loading is skipped; a 'failed' one is asked again. */
  function loadLineItemsFor(cards: readonly Invoice[]) {
    const ids = cards
      .map((inv) => inv.id)
      .filter((id) => id && (lineItemsById[id] === undefined || lineItemsById[id] === 'failed'))
    if (ids.length === 0) return
    setLineItemsById((p) => ({ ...p, ...Object.fromEntries(ids.map((id) => [id, 'loading' as const])) }))
    fetchLineItems(ids)
  }

  /**
   * ONE INLINE CODING WRITE (owner item 22). Deliberately the same data-layer
   * call the detail modal makes — `applyLineItemAccountEdit` /
   * `applyLineItemDepartmentEdit` — so there is one authority check, one
   * correction row and one cascade, not a second implementation of them.
   *
   * THE CASCADE IS THE SERVER'S. An account change CLEARS the department
   * and immediately re-derives it from the chart's implied value when the new
   * account admits exactly one, so the reply cannot be patched into the
   * cached row — the whole invoice's lines are re-read instead.
   *
   * Re-read on a REFUSAL too: nothing changed on the server, but the picker is
   * showing the option the reviewer chose, so the card would state a value the
   * record does not have. `pickerResets` remounts the pickers on that card so
   * each re-seeds from its stored value, leaving the message as the only thing
   * that changed (the same remount `InvoiceDetailModal` does).
   */
  async function writeCoding(
    inv: Invoice,
    lineItem: InvoiceLineItem,
    half: 'account' | 'department' | 'marketing-category',
    value: string,
  ) {
    const id = inv.id
    // "Account" and "Department" — what these two controls are CALLED on
    // screen (D-43), not the column names behind them. The Marketing Category
    // joined them (owner, 2026-09-15 and 2026-09-28); it is not a GL half,
    // but it is written, reported and reset the same way.
    const label = {
      account: 'Account',
      department: 'Department',
      'marketing-category': 'Marketing Category',
    }[half]
    setCodingErrors((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    setSavingLineItemId(lineItem.id)
    try {
      const { applied, reason } =
        half === 'account'
          ? await applyLineItemAccountEdit(id, lineItem.id, value, lineItem.row_version)
          : half === 'department'
            ? await applyLineItemDepartmentEdit(id, lineItem.id, value, lineItem.row_version)
            : // The DETAIL ROW's own version, never the line item's — `0` says
              // "believed not to exist yet", which is the usual case here.
              await applyLineItemMarketingDetailEdit(id, lineItem.id, value, lineItem.marketing_detail?.row_version ?? 0)
      if (!applied) {
        setCodingErrors((prev) => ({
          ...prev,
          [id]: {
            message: reason ? `The ${label} change was rejected: ${reason}` : `The ${label} change was rejected.`,
            lineItemId: lineItem.id,
            field: label,
          },
        }))
        setPickerResets((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }))
      }
      const fresh = await loadLineItems(id)
      // Only into a card that still has a cache entry: the row may have left
      // the queue while this was in flight, and resurrecting its lines would
      // leave a cache for an invoice nothing renders.
      setLineItemsById((prev) => (prev[id] === undefined ? prev : { ...prev, [id]: fresh }))
      if (applied) onCodingChanged?.()
    } catch (e) {
      setCodingErrors((prev) => ({
        ...prev,
        [id]: { message: `Couldn't save the ${label}: ${errorMessage(e)}`, lineItemId: lineItem.id, field: label },
      }))
    } finally {
      setSavingLineItemId(undefined)
    }
  }

  /**
   * A GROUP CHANGE from a card's line list (D-399): a group row's Account,
   * Department or Marketing Category, its name, Remove Group, the bulk row's
   * Combine / Ungroup / Move to Existing Group, or a drag — through the
   * `line-groups` route, as the detail modal sends it. A refusal shows on the
   * card, as a coding refusal does, and the pickers go back to what is stored.
   */
  async function writeGroup(inv: Invoice, operation: LineGroupOperation) {
    const id = inv.id
    setCodingErrors((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
    try {
      const result = await changeLineGroups(id, inv.row_version, operation)
      setLineItemsById((prev) => (prev[id] === undefined ? prev : { ...prev, [id]: result.line_items }))
      onCodingChanged?.()
    } catch (e) {
      setCodingErrors((prev) => ({
        ...prev,
        [id]: { message: `The group change was rejected: ${errorMessage(e)}`, lineItemId: '', field: 'Group' },
      }))
      setPickerResets((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }))
    }
  }

  function toggleExpand(inv: Invoice) {
    const id = inv.id
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    if (!expandedIds.has(id)) loadLineItemsFor([inv])
  }

  function toggleExpandAll() {
    if (allExpanded) {
      setExpandedIds(new Set())
      return
    }
    setExpandedIds(new Set(invoices.map((i) => i.id)))
    loadLineItemsFor(invoices)
  }

  return (
    <div aria-label="Invoice queue" ref={rootRef}>
      {/* No toolbar row at all when the host owns Expand all AND there is no
          Select all to carry — an empty bar is 32px of nothing. */}
      {(selectable || expandAll === undefined || toolbarActions) && (
        <div className={s.toolbar}>
          {/* "Unselect all" once everything is ticked (owner, 2026-09-14):
              the control toggles, and a label that only ever reads "Select
              all" describes half of what pressing it does. Same rule on
              Assignments, which carries the same checkbox. */}
          {selectable && (
            <Checkbox
              checked={allSelected}
              onChange={() => onToggleAll?.()}
              label={allSelected ? 'Unselect all' : 'Select all'}
              aria-label={allSelected ? 'Unselect all' : 'Select all'}
            />
          )}
          <div className={s.spacer} />
          {expandAll === undefined && (
            <Button
              appearance="subtle"
              // Medium and regular-weight, matching the per-group Expand all on
              // Export Control and Closing Confirmation AND the Select all
              // checkbox at the other end of this row (`useQueueToolbarButtonStyles`).
              // It was `size="small"`, which made the same control 12px here and
              // 14px there.
              className={toolbarBtn.subtle}
              icon={allExpanded ? <ChevronDoubleUpRegular /> : <ChevronDoubleDownRegular />}
              onClick={toggleExpandAll}
            >
              {allExpanded ? 'Collapse All' : 'Expand All'}
            </Button>
          )}
          {toolbarActions}
        </div>
      )}

      <div className={s.list} role="list">
        {invoices.map((inv) => {
          const id = inv.id
          const expanded = expandedIds.has(id)
          const days = ageInDays(inv.invoice_date)
          const flagTypes = unresolvedFlagTypes(inv)
          const unresolvedCount = inv.unresolved_flag_count ?? flagTypes.length
          const selectionVeto = selectionDisabled?.(inv)
          const dimmed = dimmedIds?.has(id)
          const held = heldIds?.has(id)
          // Matches what the row itself prints two hundred lines below
          // (`inv.vendor_name ?? 'Vendor unknown'`) — an unmatched vendor used
          // to read "Show line items for invoice" to a screen reader while the
          // row said "Vendor unknown", naming the same card two different ways.
          const vendorLabel = inv.vendor_name ?? 'Vendor unknown'
          const uncoded = uncodedLinesFor?.(inv)
          return (
            <div key={id} className={mergeClasses(s.card, dimmed && s.dimmed)} role="listitem">
              {/* The header is a plain container, not a button: it holds the
                  selection checkbox, the expander and the row actions, and a
                  button may not contain other controls. Clicking the empty
                  space still expands the card; the chevron button is the
                  keyboard/screen-reader route. */}
              <div
                className={mergeClasses(s.head, expanded && s.headOpen)}
                onClick={() => toggleExpand(inv)}
              >
                {selectable && (
                  <span title={selectionVeto} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds?.has(id) ?? false}
                      onChange={() => onToggle?.(id)}
                      disabled={!!selectionVeto}
                      // The veto rides the accessible name: a disabled checkbox
                      // is not focusable, so a title tooltip alone never
                      // reaches anyone who isn't hovering it.
                      // NAMED BY THE RECORD, not by the control. Every checkbox
                      // on the queue read "Select invoice", so a screen-reader
                      // user moving down 27 of them heard the same three words
                      // 27 times with nothing to tell them apart.
                      aria-label={
                        selectionVeto
                          ? `Select ${vendorLabel} ${inv.external_invoice_id ?? ''} — unavailable: ${selectionVeto}`.replace(/\s+/g, ' ')
                          : `Select ${vendorLabel} ${inv.external_invoice_id ?? ''}`.replace(/\s+/g, ' ').trim()
                      }
                    />
                  </span>
                )}
                <Button
                  appearance="subtle"
                  size="small"
                  className={s.chevBtn}
                  aria-expanded={expanded}
                  aria-label={`${expanded ? 'Hide' : 'Show'} line items for ${vendorLabel}`}
                  icon={
                    <span className={mergeClasses(s.chev, expanded && s.chevOpen)}>
                      <ChevronRightRegular />
                    </span>
                  }
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleExpand(inv)
                  }}
                />
                <div className={s.tgroup}>
                  {/* Words, not dashes: with the everything-gets-added rule a
                      junk scan legitimately arrives with neither, and a card
                      of bare em-dashes reads as broken rather than unresolved. */}
                  <span className={s.eyebrow}>{inv.company_name ?? 'Company unknown'}</span>
                  <span className={s.vendor}>{inv.vendor_name ?? 'Vendor unknown'}</span>
                  <span className={s.meta}>
                    <span>{formatDate(inv.invoice_date)}</span>
                    {/* A FINISHED INVOICE SHOWS ITS OUTCOME WHERE THE AGE WAS
                        (owner, 2026-09-28: "drop the pill", with the status
                        pill moved into its place). Its age stopped mattering
                        when it left the pipeline, and the red count read as
                        overdue on a record that is done. Archives spans five
                        terminal statuses, so the badge says which one; a step
                        queue is single-status and keeps its age. */}
                    {columns === 'amounts' &&
                      ((TERMINAL_STATUSES as readonly string[]).includes(inv.status ?? '') ? (
                        <StatusBadge status={inv.status} />
                      ) : (
                        <AgePill days={days} />
                      ))}
                    {/* Department/Director/Oversight Approval's coding-gate
                        pill — omitted when the queue isn't coding-gated, or
                        when this row has nothing left to code (D-9). */}
                    {columns === 'amounts' && uncoded && uncoded.count > 0 && (
                      <UncodedLinesPill count={uncoded.count} title={uncoded.tooltip} />
                    )}
                    {/* Hold state in words — the dimming and the amber lock
                        glyph carry no text alternative on their own. */}
                    {held && (
                      <DotPill pill={TINT_PILLS.yellow} title={selectionVeto}>
                        {HELD_PILL}
                      </DotPill>
                    )}
                  </span>
                </div>
                <div className={s.rgroup}>
                  {columns === 'intake' ? (
                    <>
                      <AgePill days={days} />
                      {/* Nothing at all under `intakeBadge='none'` — see the
                          prop for why Import Control asks for that rather
                          than the status badge (owner item 14, 2026-08-31). */}
                      {intakeBadge === 'flags' && (
                        <UnresolvedFlagsPill
                          count={unresolvedCount}
                          title={unresolvedFlagLabels(inv).join(', ') || undefined}
                        />
                      )}
                      {intakeBadge === 'status' && <StatusBadge status={inv.status} />}
                    </>
                  ) : (
                    <div className={s.amounts}>
                      {/* Total only — the Subtotal + Tax breakdown that used to
                          print here duplicated ReconcileFooter's line inside the
                          expanded card and read as noise in the collapsed
                          gallery (owner feedback, 2026-08-14: "still seeing
                          '$1,034.23 + $0.00 tax' under the invoice total"). The
                          breakdown still lives in the expanded card via
                          InvoiceLineItemsTable. */}
                      <span className={s.totalLine}>{formatCurrency(inv.total)}</span>
                    </div>
                  )}
                  {hasActions && (
                    <div className={s.actions} onClick={(e) => e.stopPropagation()}>
                      {rowActions?.(inv)}
                      {onOpen && (
                        <Button size="small" onClick={() => onOpen(inv)}>
                          Open
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {/* HIDDEN FROM THE ACCESSIBILITY TREE WHILE COLLAPSED, not just
                  clipped. The line-item table renders its own "Loading line
                  items…" spinner (`role="progressbar"`) until a row is expanded
                  and its items fetched, and a collapsed row never fetches — so
                  every card on the queue carried a live progressbar, 27 of them
                  on a full Import Control queue, announced to a screen reader as
                  perpetual loading. `s.exp` only sets `height: 0; overflow:
                  hidden`, which hides pixels and nothing else. `inert` also
                  takes the controls inside out of the tab order, which they had
                  no business being in while invisible. */}
              <div
                className={mergeClasses(s.exp, expanded && s.expOpen)}
                aria-hidden={!expanded}
                inert={!expanded}
              >
                <div className={s.clip}>
                  <div className={s.lineItemsWrap}>
                    {/* The refusal, on the card it was refused on: a notification, so
                        it is announced, and the description of the picker it
                        was refused on (QA 2026-10-04, C1-09). */}
                    {codingErrors[id] && (
                      <div className={s.codingError}>
                        <ErrorBanner id={codingErrorId(id)} alert message={codingErrors[id].message} />
                      </div>
                    )}
                    {lineItemsById[id] === 'failed' ? (
                      <ErrorBanner
                        message="Couldn't load the line items."
                        onRetry={() => loadLineItemsFor([inv])}
                      />
                    ) : (
                    <InvoiceLineItemsTable
                      lineItems={lineItemsById[id]}
                      screen={screen}
                      subtotal={inv.subtotal}
                      tax={inv.tax}
                      shipping={inv.shipping}
                      total={inv.total}
                      codingDisplay={codingDisplay}
                      // OWNER ITEM 22. Passing a callback is what turns the
                      // column into a picker (`InvoiceLineItemsTable`'s own
                      // gate), so a step that grants neither half passes
                      // nothing. A held row is edited like any other (D-282).
                      onAccountChange={
                        editAccounts && glCodeEditable(screen).account
                          ? (lineItem, accountId) =>
                              void writeCoding(inv, lineItem, 'account', accountId)
                          : undefined
                      }
                      onDepartmentChange={
                        editDepartments && glCodeEditable(screen).department
                          ? (lineItem, departmentId) =>
                              void writeCoding(inv, lineItem, 'department', departmentId)
                          : undefined
                      }
                      // THE ADDITIONAL DETAIL ARE NOT A PROP (owner,
                      // 2026-09-25 — D-153, D-174). They are editable wherever
                      // the server grants them (D-192), read off this
                      // queue's own `screen` through the one shared rule, so no
                      // mount can forget it; the table decides line by line.
                      invoice={inv}
                      onCategoryChange={
                        categoryPickableAt(screen)
                          ? (lineItem, categoryId) => void writeCoding(inv, lineItem, 'marketing-category', categoryId)
                          : undefined
                      }
                      // Where grouping may change (G-12), as the detail modal decides it.
                      onGroupOperation={
                        groupingOpen(inv.status) && (editAccounts || editDepartments)
                          ? (operation) => writeGroup(inv, operation)
                          : undefined
                      }
                      // Every Description is locked on the card, a group's too (D-409).
                      groupNamesEditable={false}
                      savingLineItemId={savingLineItemId}
                      pickerResetNonce={pickerResets[id] ?? 0}
                      refusal={
                        codingErrors[id]
                          ? { lineItemId: codingErrors[id].lineItemId, field: codingErrors[id].field, messageId: codingErrorId(id) }
                          : undefined
                      }
                      pickerMountNode={pickerHost}
                    />
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
